import React, { useState, useEffect } from "react";
import { Button, Skeleton, Tag, Tooltip, Modal, Card } from "antd";
import { useNavigate } from 'react-router-dom';
import coreAPIService from "../apiServices/coreAPIService";
import { CTW_API_ENDPOINT } from "../utils";
import { toast } from "../common/Popover";
import Heading from "../common/Heading";
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { PlayCircleOutlined, PauseCircleOutlined, DeleteOutlined, GlobalOutlined, EditOutlined, SyncOutlined, EyeOutlined, PlusOutlined, InfoCircleOutlined, CheckCircleOutlined, CopyOutlined, MessageOutlined, WhatsAppOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Select, Space, Typography } from "antd";
import { useAuthOrg } from "../context/AuthOrgContext";
import { useMetaSetup } from "../context/MetaSetupContext";
import CommonLayout from "../common/LayoutBlock";
import { MetaCampaign, MetaAd } from "./MetaCampaignManagement";
import AdPreviewModal from "../components/campaign/AdPreviewModal";
import AddNewAdModal from "../components/campaign/AddNewAdModal";

const { Text } = Typography;
const { Option } = Select;

const MetaCampaignManagementGrid: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const [loading, setLoading] = useState<boolean>(false);
    const [campaigns, setCampaigns] = useState<MetaCampaign[]>([]);
    const navigate = useNavigate();
    const { currentOrg, availableOrgs } = useAuthOrg();
    const [orgId, setOrgId] = useState<string | null>(currentOrg?.orgId || null);
    const { metaConnectStatus } = useMetaSetup();
    const [expandedCampaignId, setExpandedCampaignId] = useState<number | null>(null);
    const [currentCampaign, setCurrentCampaign] = useState<MetaCampaign | null>(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [selectedAd, setSelectedAd] = useState<MetaAd | null>(null);
    const [addNewAdModalVisible, setAddNewAdModalVisible] = useState(false);
    const [selectedMetaAdsetId, setSelectedMetaAdsetId] = useState<string>('');

    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg cursor-pointer" onClick={() => navigate("/campaign-management")}>Campaign Management</div>,
            },
        ]);
        fetchCampaignData();
    }, [setBreadcrumbs, navigate, orgId]);

    const fetchCampaignData = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const res = await coreAPIService.get<any>(CTW_API_ENDPOINT, 'v2/org/times-panel/campaigns/' + orgId);
            setLoading(false);
            if (res.meta && res.meta.code === 200) {
                const filteredData = res.data.filter((c: MetaCampaign) => c.campaignId);
                setCampaigns(filteredData);
                if (expandedCampaignId) {
                    const campaign = filteredData.find((c: MetaCampaign) => c.id === expandedCampaignId);
                    setCurrentCampaign(campaign || null);
                }
            } else {
                toast("warning", "Failed to fetch campaigns.");
            }
        } catch (error) {
            setLoading(false);
            toast("error", "Error occurred while fetching campaigns.");
        } finally {
            setLoading(false);
        }
    };

    const putCampaignStatus = async (campaignId: string, action: 'activate' | 'pause') => {
        setLoading(true);
        try {
            const endpoint = action === 'activate' ? 'activate' : 'pause';
            const res = await coreAPIService.post<any>(CTW_API_ENDPOINT, `v2/meta-integration/${campaignId}/${endpoint}`);
            setLoading(false);
            if (res.meta.status === true) {
                toast("success", `Campaign ${action === 'activate' ? 'activated' : 'paused'} successfully.`);
                fetchCampaignData();
            } else {
                toast("warning", `Failed to ${action} campaign.`);
            }
        } catch (error) {
            setLoading(false);
            toast("error", `Error occurred while ${action === 'activate' ? 'activating' : 'pausing'} campaign.`);
        }
    };

    const deleteCampaign = async (id: number) => {
        Modal.confirm({
            title: 'Delete Campaign',
            content: 'Are you sure you want to delete this campaign? This action cannot be undone and will delete the campaign on meta itself.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await coreAPIService.delete<any>(CTW_API_ENDPOINT, `v2/org/times-panel/campaigns/${id}`);
                    setLoading(false);
                    if (res.meta.status === true) {
                        toast("success", "Campaign deleted successfully.");
                        fetchCampaignData();
                    } else {
                        toast("warning", "Failed to delete campaign.");
                    }
                } catch (error) {
                    setLoading(false);
                    toast("error", "Error occurred while deleting campaign.");
                }
            },
        });
    };

    const deleteAd = async (id: string, campaign: MetaCampaign) => {
        Modal.confirm({
            title: 'Delete Ad',
            content: 'Are you sure you want to delete this ad? This action cannot be undone and will delete the ad on meta itself.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await coreAPIService.delete<any>(CTW_API_ENDPOINT, `v2/org/times-panel/ads/${id}`);
                    setLoading(false);
                    if (res.meta.status === true) {
                        toast("success", "Ad deleted successfully.");
                        fetchCampaignData();
                        setExpandedCampaignId(campaign.id);
                        setCurrentCampaign(campaign);
                    } else {
                        toast("warning", "Failed to delete ad.");
                    }
                } catch (error) {
                    setLoading(false);
                    toast("error", "Error occurred while deleting ad.");
                }
            },
        });
    };

    const handleEditCampaign = (campaign: MetaCampaign) => {
        if (expandedCampaignId === campaign.id) {
            // Collapse if already expanded
            setExpandedCampaignId(null);
            setCurrentCampaign(null);
        } else {
            // Expand
            setExpandedCampaignId(campaign.id);
            setCurrentCampaign(campaign);
        }
    };

    const handleCloneCampaign = (campaign: MetaCampaign) => {
        navigate(`create-campaign?clone=${campaign.id}`, { state: { campaignData: campaign, isCloneMode: true } })
    };

    const handleChatWithBot = (campaign: MetaCampaign) => {
        const prefilledText = campaign.ads?.[0]?.prefilledMessage || "Hello";
        window.open(`https://wa.me/${campaign.wabaNumber}?text=${encodeURIComponent(prefilledText)}`, "_blank");
    }

    const handleEditAdset = (campaign: MetaCampaign) => {
        navigate(`create-campaign?edit=${campaign.id}`, { state: { campaignData: campaign, isEditMode: true } })
        // toast("info", `Edit adset: ${campaign.adsetName}`);
        // TODO: Implement edit adset functionality
    };

    const handleAddNewAd = () => {
        if (currentCampaign?.metaAdsetId) {
            setSelectedMetaAdsetId(currentCampaign.metaAdsetId);
            setAddNewAdModalVisible(true);
        } else {
            toast("warning", "No adset ID found for this campaign");
        }
    };

    const handleAddNewAdModalClose = () => {
        setAddNewAdModalVisible(false);
        setSelectedMetaAdsetId('');
    };

    const handleAddNewAdSuccess = () => {
        fetchCampaignData(); // Refresh the campaign data to show the new ad
        setCurrentCampaign(null);
        setExpandedCampaignId(null);
    };

    const handleAdClick = (ad: MetaAd) => {
        setSelectedAd(ad);
        setPreviewVisible(true);
    };

    const handlePreviewClose = () => {
        setPreviewVisible(false);
        setSelectedAd(null);
    };

    const updateAdStatus = async (adId: string, status: 'ACTIVE' | 'PAUSED') => {
        Modal.confirm({
            title: 'Change Ad status',
            content: `Are you sure you want to ${status === 'ACTIVE' ? 'activate' : 'pause'} this ad?`,
            okText: status === 'ACTIVE' ? 'Activate' : 'Pause',
            okType: status === 'ACTIVE' ? 'primary' : 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setLoading(true);
                if (adId === '') {
                    toast("warning", "Ad ID is required to update status.");
                    setLoading(false);
                    return;
                }
                try {
                    const res = await coreAPIService.post<any>(CTW_API_ENDPOINT, 'v2/meta-integration/update-ad-status', {
                        adId,
                        status
                    });
                    setLoading(false);
                    if (res.code === 200 || res.meta?.code === 200) {
                        toast("success", `Ad ${status === 'ACTIVE' ? 'activated' : 'paused'} successfully.`);
                        fetchCampaignData();
                        setCurrentCampaign(null);
                    } else {
                        toast("warning", `Failed to ${status === 'ACTIVE' ? 'activate' : 'pause'} ad.`);
                    }
                } catch (error) {
                    setLoading(false);
                    toast("error", `Error occurred while updating ad status.`);
                }
            }
        })
    };

    const switchOrg = (orgId: string) => {
        setOrgId(orgId);
    };

    const getStatusColor = (status: string) => {
        const lowerStatus = status?.toLowerCase();
        if (lowerStatus === "active") return "green";
        if (lowerStatus === "paused") return "orange";
        if (lowerStatus === "creating") return "blue";
        if (lowerStatus?.includes("error") || lowerStatus?.includes("fail")) return "red";
        return "blue";
    };

    const renderTargeting = (campaign: MetaCampaign) => {
        try {
            const targeting = typeof campaign.targeting === 'string'
                ? JSON.parse(campaign.targeting)
                : campaign.targeting;

            if (!targeting) return <span className="text-xs text-gray-500">None</span>;

            const audience = targeting;

            return (
                <div className="space-y-3 text-xs">
                    {/* Advantage plus */}
                    <div>
                        <div className="font-semibold text-gray-600 mb-1">Advantage +</div>
                        <div className="text-gray-700">
                            {campaign.advantagePlus ? "Yes" : "No"}
                        </div>
                    </div>
                    {/* Audiences */}
                    <div>
                        <div className="font-semibold text-gray-600 mb-1">Audiences Id</div>
                        <div className="text-gray-700">
                            {audience.custom_audiences?.length
                                ? audience.custom_audiences.map((a: any) => a.id || a).join(", ")
                                : "None"}
                        </div>
                    </div>

                    {/* Age */}
                    <div>
                        <div className="font-semibold text-gray-600 mb-1">Age</div>
                        <div className="text-gray-700">
                            {audience.age_min} – {audience.age_max}
                        </div>
                    </div>

                    {/* Included Locations */}
                    <div>
                        <div className="font-semibold text-gray-600 mb-1">Included Locations</div>
                        <div className="text-gray-700">
                            <div>Cities: {audience.geo_locations?.cities?.length
                                ? audience.geo_locations.cities.map((c: any) => c.name).join(", ")
                                : "—"}</div>
                            <div>Types: {audience.geo_locations?.location_types?.length
                                ? audience.geo_locations.location_types.join(", ")
                                : "—"}</div>
                        </div>
                    </div>

                    {/* Excluded Locations */}
                    {audience.excluded_geo_locations && (
                        <div>
                            <div className="font-semibold text-red-600 mb-1">Excluded Locations</div>
                            <div className="text-gray-700">
                                <div>Cities: {audience.excluded_geo_locations.cities?.length
                                    ? audience.excluded_geo_locations.cities.map((c: any) => c.name).join(", ")
                                    : "—"}</div>
                                <div>Regions: {audience.excluded_geo_locations.regions?.length
                                    ? audience.excluded_geo_locations.regions.map((r: any) => r.name).join(", ")
                                    : "—"}</div>
                            </div>
                        </div>
                    )}

                    {/* Platforms */}
                    <div>
                        <div className="font-semibold text-gray-600 mb-1">Platforms</div>
                        <div className="text-gray-700">
                            {audience.publisher_platforms?.length
                                ? audience.publisher_platforms.join(", ")
                                : "—"}
                        </div>
                    </div>
                </div>
            );
        } catch (e) {
            return <span className="text-red-500 text-xs">Error parsing targeting</span>;
        }
    };

    return (
        <>
            <CommonLayout>
                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <Heading title="Campaign Management" />
                    <div className="flex flex-wrap gap-4 items-center">
                        <Space direction="horizontal">
                            <Text strong><GlobalOutlined /> Account</Text>
                            <Select
                                value={orgId}
                                onChange={switchOrg}
                                style={{ width: 200 }}
                                placeholder="Select Account"
                            >
                                {availableOrgs.map((org) => (
                                    <Option key={org.orgId} value={org.orgId}>
                                        {org.orgName}
                                    </Option>
                                ))}
                            </Select>
                        </Space>

                        <Tooltip title={!metaConnectStatus ? "Connect Meta account to create campaigns" : ""}>
                            <Button
                                type="primary"
                                onClick={() => navigate('create-campaign')}
                                disabled={!metaConnectStatus}
                                className="bg-[#1F344C]"
                            >
                                Create Campaign
                            </Button>
                        </Tooltip>
                        <Button
                            type="primary"
                            className="bg-[#1F344C]"
                            onClick={() => fetchCampaignData()}
                        >
                            <SyncOutlined spin={loading} />Refresh
                        </Button>
                    </div>
                </div>

                <div className="bg-gray-50 p-6">
                    {loading ? (
                        <div className="space-y-4">
                            {[...Array(5)].map((_, index) => (
                                <div key={index} className="bg-white p-4 rounded-lg shadow">
                                    <Skeleton active />
                                </div>
                            ))}
                        </div>
                    ) : campaigns.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-gray-500 text-lg">No campaigns found</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {campaigns.map((campaign) => {
                                const hasError = campaign.campaignError || campaign.adsetError || !campaign.ads.some(ad => !ad.adError);

                                // Aggregated Ad Status Logic
                                let aggregatedAdStatus = "Unknown";
                                if (campaign?.ads?.length > 0) {
                                    const anyActive = campaign.ads.some(ad => ad.adStatus?.toLowerCase() === 'active');
                                    if (anyActive && campaign.campaignStatus?.toLowerCase() === 'active') aggregatedAdStatus = "ACTIVE";
                                    else {
                                        const allPaused = campaign.ads.every(ad => ad.adStatus?.toLowerCase() === 'paused');
                                        if (allPaused && campaign.campaignStatus?.toLowerCase() === 'paused') aggregatedAdStatus = "PAUSED";
                                        else aggregatedAdStatus = campaign.ads[0].adStatus || "Unknown";
                                    }
                                    if (campaign.campaignError || campaign.adsetError || campaign.ads.some(ad => ad.adError)) aggregatedAdStatus = "failed";
                                } else {
                                    aggregatedAdStatus = "failed";
                                }

                                const adStatus = aggregatedAdStatus.toLowerCase();
                                const isPaused = adStatus === "paused";
                                const isActive = adStatus === "active";
                                const campaignEnded = campaign.endTime ? campaign.endTime < new Date().toISOString() : false;
                                const playPauseDisabled = campaignEnded || hasError || (!isPaused && !isActive);
                                const isExpanded = expandedCampaignId === campaign.id;

                                return (
                                    <div key={campaign.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
                                        {/* Campaign Row */}
                                        <div className="p-4">
                                            <div className="grid grid-cols-12 gap-4 items-center">

                                                {/* Status */}
                                                <div className="col-span-12 md:col-span-1 text-center">
                                                    <div className="text-xs text-gray-500 mb-1">Status</div>
                                                    <Tag color={getStatusColor(campaign.campaignStatus)} className="text-xs">
                                                        {campaign.campaignStatus}
                                                    </Tag>
                                                </div>

                                                {/* Campaign Name & Actions */}
                                                <div className="col-span-12 md:col-span-3">
                                                    <div className="flex items-center gap-2">
                                                        <a
                                                            className={`${!campaign.adsetError && !campaign.campaignError && !campaign.ads.some(ad => ad.adError) ? "text-blue-600 hover:underline font-semibold cursor-pointer" : ""}`}
                                                            onClick={() => !campaign.adsetError && !campaign.campaignError && !campaign.ads.some(ad => ad.adError) && navigate(`/ctc-ads-meta/ad-analytics?orgId=${orgId}&adAccountId=${campaign.adAccountId}&campaignId=${campaign.campaignId}`)}
                                                        >
                                                            {campaign.campaignName}
                                                        </a>
                                                        {/* <span
                                                            className="font-semibold"
                                                        // onClick={() => navigate(`/ctc-ads-meta/ad-analytics?orgId=${orgId}&adAccountId=${campaign.adAccountId}&campaignId=${campaign.campaignId}`)}
                                                        >
                                                            {campaign.campaignName}
                                                        </span> */}
                                                        {campaign.campaignError || campaign.adsetError || campaign.ads.some(ad => ad.adError) ? <Tooltip title={campaign.campaignError || campaign.adsetError || campaign.ads.map(ad => ad.adError)}>
                                                            <InfoCircleOutlined style={{ color: getStatusColor(adStatus) }} />
                                                        </Tooltip> : <CheckCircleOutlined style={{ color: getStatusColor(adStatus) }} />}
                                                        {campaign.endTime && campaign.endTime < new Date().toISOString() && (
                                                            <Tooltip title="Campaign end date has passed">
                                                                <ExclamationCircleOutlined style={{ color: "red" }} />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-1 mt-2">
                                                        <Tooltip title={campaign.campaignStatus.toLowerCase() === "paused" ? "Resume" : campaign.campaignStatus.toLowerCase() === "active" ? "Pause" : "Not Available"}>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                onClick={() => putCampaignStatus(campaign.campaignId || "", campaign.campaignStatus.toLowerCase() === "paused" ? 'activate' : 'pause')}
                                                                disabled={playPauseDisabled}
                                                                icon={campaign.campaignStatus.toLowerCase() === "paused" ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title={!campaign.campaignError && !campaign.adsetError ? "Edit" : "Error while creating campaign"}>
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<EditOutlined />}
                                                                onClick={() => handleEditCampaign(campaign)}
                                                                disabled={!!campaign.campaignError || !!campaign.adsetError}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="Test bot journey">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<WhatsAppOutlined />}
                                                                onClick={() => handleChatWithBot(campaign)}
                                                                disabled={campaign.wabaNumber?.length === 0}
                                                            />
                                                        </Tooltip>

                                                        <Tooltip title="Clone">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<CopyOutlined />}
                                                                onClick={() => handleCloneCampaign(campaign)}
                                                            />
                                                        </Tooltip>
                                                        {hasError && (
                                                            <Tooltip title="Delete">
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    onClick={() => deleteCampaign(campaign.id)}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Account */}
                                                <div className="col-span-6 md:col-span-2 text-center">
                                                    {/* <div className="text-xs text-gray-500 mb-1">Account</div> */}
                                                    <Tag color="blue" className="max-w-[200px] text-wrap">{availableOrgs.find(o => o.orgId === orgId)?.orgName || "Unknown"}</Tag>
                                                </div>

                                                {/* Start Date */}
                                                <div className="col-span-6 md:col-span-2 text-center">
                                                    <div className="text-xs text-gray-500 mb-1">Start Date</div>
                                                    <div className="text-sm">{campaign.startTime.split("T")[0]}</div>
                                                </div>

                                                {/* End Date */}
                                                <div className="col-span-6 md:col-span-2 text-center">
                                                    <div className="text-xs text-gray-500 mb-1">End Date</div>
                                                    <div className="text-sm">{campaign.endTime ? campaign.endTime.split("T")[0] : "None"}</div>
                                                </div>

                                                {/* Budget */}
                                                <div className="col-span-6 md:col-span-2 text-center">
                                                    <div className="text-xs text-gray-500 mb-1">Lifetime | Daily Budget</div>
                                                    <div className="text-sm">₹{campaign.lifetimeBudget || "N/A"} | ₹{campaign.dailyBudget || "N/A"}</div>
                                                </div>

                                            </div>
                                        </div>

                                        {/* Expanded Section - Adset on Left, Ads on Right */}
                                        {isExpanded && currentCampaign && (
                                            <div className="border-t border-gray-200 bg-gray-50 p-4">
                                                <div className="grid grid-cols-12 gap-4">
                                                    {/* Left Side - Adset with Targeting */}
                                                    <div className="col-span-12 lg:col-span-4 border-r border-gray-200 pr-4">
                                                        <div className="space-y-4">
                                                            <Card className="shadow-md">
                                                                <div className="space-y-3">
                                                                    {/* Adset Header */}
                                                                    <div className="flex justify-between items-start border-b pb-2">
                                                                        <div>
                                                                            <h4 className="font-semibold text-base">{currentCampaign.campaignName}</h4>
                                                                            <Tag color={getStatusColor(currentCampaign.adsetStatus)} className="text-xs mt-1">
                                                                                {currentCampaign.adsetStatus}
                                                                            </Tag>
                                                                            {currentCampaign.adsetError && (
                                                                                <div className="text-red-500 text-xs mt-1">{currentCampaign.adsetError}</div>
                                                                            )}
                                                                        </div>
                                                                        <Button
                                                                            type="text"
                                                                            size="small"
                                                                            icon={<EditOutlined />}
                                                                            onClick={() => handleEditAdset(currentCampaign)}
                                                                        />
                                                                    </div>

                                                                    {/* Adset Details */}
                                                                    <div className="space-y-2 text-sm">
                                                                        {currentCampaign.dailyBudget && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-gray-500">Daily Budget:</span>
                                                                                <span className="font-medium">₹{currentCampaign.dailyBudget}</span>
                                                                            </div>
                                                                        )}
                                                                        {currentCampaign.lifetimeBudget && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-gray-500">Lifetime Budget:</span>
                                                                                <span className="font-medium">₹{currentCampaign.lifetimeBudget}</span>
                                                                            </div>
                                                                        )}
                                                                        <div className="flex justify-between">
                                                                            <span className="text-gray-500">Start:</span>
                                                                            <span>{currentCampaign.startTime.split('T')[0]}</span>
                                                                        </div>
                                                                        {currentCampaign.endTime && (
                                                                            <div className="flex justify-between">
                                                                                <span className="text-gray-500">End:</span>
                                                                                <span>{currentCampaign.endTime.split('T')[0]}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Targeting Information */}
                                                                    <div className="border-t pt-3">
                                                                        <h5 className="text-xs font-bold text-gray-700 uppercase mb-2">Targeting</h5>
                                                                        {renderTargeting(currentCampaign)}
                                                                    </div>
                                                                </div>
                                                            </Card>
                                                        </div>
                                                    </div>

                                                    {/* Right Side - Ads in Vertical List */}
                                                    <div className="col-span-12 lg:col-span-8 h-[calc(100vh-350px)] overflow-y-auto">
                                                        <div className="space-y-3">
                                                            {/* Ads List */}
                                                            {currentCampaign.ads.map((ad, idx) => (
                                                                <Card
                                                                    key={idx}
                                                                    hoverable
                                                                    className="shadow hover:shadow-md transition-shadow"
                                                                >
                                                                    <div className="grid grid-cols-12 gap-4 items-center">
                                                                        {/* Ad Image */}
                                                                        <div className="col-span-12 sm:col-span-3">
                                                                            {ad.adCreativeImageLink || ad.adVideoThumbnailUrl ? (
                                                                                <div className="relative h-24 overflow-hidden rounded">
                                                                                    <img
                                                                                        alt={ad.adName}
                                                                                        src={ad.adCreativeImageLink || ad.adVideoThumbnailUrl || ""}
                                                                                        className="w-full h-full object-cover"
                                                                                        onError={(e) => {
                                                                                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x150?text=No+Image';
                                                                                        }}
                                                                                    />
                                                                                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                                                                                        <EyeOutlined className="text-white text-xl opacity-0 hover:opacity-100 transition-opacity duration-300" />
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="h-24 bg-gray-200 flex items-center justify-center rounded">
                                                                                    <span className="text-gray-400 text-xs">No Image</span>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Ad Details */}
                                                                        <div className="col-span-12 sm:col-span-9">
                                                                            <div className="space-y-2">
                                                                                <div className="flex justify-between items-start">
                                                                                    <h5 className="font-semibold text-sm cursor-pointer hover:text-blue-600" onClick={() => handleAdClick(ad)}>{ad.adName}</h5>
                                                                                    <div className="flex items-center gap-2">
                                                                                        <Tag color={getStatusColor(ad.adStatus)} className="text-xs">
                                                                                            {ad.adStatus}
                                                                                        </Tag>
                                                                                        {ad.metaAdId && (
                                                                                            <Tooltip title={ad.adStatus?.toLowerCase() === 'paused' ? 'Activate Ad' : 'Pause Ad'}>
                                                                                                <Button
                                                                                                    type="text"
                                                                                                    size="small"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        updateAdStatus(
                                                                                                            ad.metaAdId || "",
                                                                                                            ad.adStatus?.toLowerCase() === 'paused' ? 'ACTIVE' : 'PAUSED'
                                                                                                        );
                                                                                                    }}
                                                                                                    disabled={ad.adError || !ad.metaAdId}
                                                                                                    icon={ad.adStatus?.toLowerCase() === 'paused' ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                                                                                                />
                                                                                            </Tooltip>
                                                                                        )}
                                                                                        {ad.adId && ad.adError && (
                                                                                            <Tooltip title="Delete">
                                                                                                <Button
                                                                                                    type="text"
                                                                                                    size="small"
                                                                                                    danger
                                                                                                    icon={<DeleteOutlined />}
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        deleteAd(ad.adId || "", campaign);
                                                                                                    }}
                                                                                                />
                                                                                            </Tooltip>
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                                {ad.adError && (
                                                                                    <div className="text-red-500 text-xs bg-red-50 p-1 rounded">
                                                                                        Error: {ad.adError}
                                                                                    </div>
                                                                                )}

                                                                                {ad.adCreativeMessage && (
                                                                                    <p className="text-xs text-gray-600 line-clamp-2">
                                                                                        {ad.adCreativeMessage}
                                                                                    </p>
                                                                                )}

                                                                                {ad.adCreativeLinkName && (
                                                                                    <div className="text-xs">
                                                                                        <span className="font-medium text-gray-700">{ad.adCreativeLinkName}</span>
                                                                                        {ad.adCreativeLinkMessage && (
                                                                                            <span className="text-gray-500 ml-2">• {ad.adCreativeLinkMessage}</span>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Card>
                                                            ))}

                                                            {/* Add New Ad Card */}
                                                            <Card
                                                                hoverable
                                                                className="shadow hover:shadow-md transition-shadow border-2 border-dashed border-blue-300 bg-blue-50 cursor-pointer"
                                                                onClick={handleAddNewAd}
                                                            >
                                                                <div className="flex items-center justify-center py-4 text-blue-600">
                                                                    <PlusOutlined className="text-xl mr-2" />
                                                                    <span className="font-semibold">Add New Ad</span>
                                                                </div>
                                                            </Card>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </CommonLayout>

            <AdPreviewModal
                visible={previewVisible}
                onClose={handlePreviewClose}
                adData={selectedAd}
            />

            <AddNewAdModal
                campaignName={currentCampaign?.campaignName || ''}
                adNum={currentCampaign?.ads?.length || 0}
                visible={addNewAdModalVisible}
                onClose={handleAddNewAdModalClose}
                metaAdsetId={selectedMetaAdsetId}
                orgId={currentCampaign?.orgId || ''}
                onSuccess={handleAddNewAdSuccess}
                adAccountId={currentCampaign?.adAccountId || ''}
            />
        </>
    );
};

export default MetaCampaignManagementGrid;
